import { ReactElement, createContext, useEffect, useMemo, useState } from 'react';
import { MobileAppConfigInterface, MobileAppContextInterface } from '../services/exports/Interfaces';
import {
  DeviceOS,
  ENVIRONMENTS,
  OnlineShopWebViewMessageTypes,
  PushNotificationStatus,
} from '../services/exports/Constants';

declare global {
  interface Window {
    mobileAppConfig?: MobileAppConfigInterface;
  }
}

interface Props {
  children: ReactElement;
}

const defaultContext: MobileAppContextInterface = {
  isMobileApp: false,
  device: null,
  isIos: false,
  isAndroid: false,
  wallets: {
    isApplePaySupported: false,
    isGooglePaySupported: false,
  },
  pushNotificationsStatus: PushNotificationStatus.Undetermined,
  insets: {
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
  },
};

export const MobileAppContext: React.Context<MobileAppContextInterface> = createContext(defaultContext);

export default function MobileAppProvider(props: Props): ReactElement {
  const { children } = props;
  const searchParams = new URLSearchParams(window.location.search);
  const isFakeMobileApp = import.meta.env.VITE_MODE !== ENVIRONMENTS.PROD && searchParams.get('mobile_app') === 'true';
  const config = window.mobileAppConfig;
  const [pushNotificationsStatus, setPushNotificationsStatus] = useState(PushNotificationStatus.Undetermined);

  const context = useMemo(() => {
    if (isFakeMobileApp && !config) {
      return {
        ...defaultContext,
        isMobileApp: true,
        isIos: false,
        isAndroid: false,
        device: {} as any,
        wallets: {
          isApplePaySupported: false,
          isGooglePaySupported: false,
        },
        pushNotificationsStatus,
      };
    }

    if (config) {
      return {
        ...config,
        isMobileApp: true,
        isIos: config.device.os === DeviceOS.iOS,
        isAndroid: config.device.os === DeviceOS.Android,
        pushNotificationsStatus,
      };
    }

    return defaultContext;
  }, [isFakeMobileApp, config, pushNotificationsStatus]);

  useEffect(() => {
    const root = document.documentElement;
    if (!root || !context.isMobileApp) return;
    root.style.setProperty('--ion-safe-area-top', `${context.insets.top}px`);
    root.style.setProperty('--ion-safe-area-right', `${context.insets.right}px`);
    root.style.setProperty('--ion-safe-area-bottom', `${context.insets.bottom}px`);
    root.style.setProperty('--ion-safe-area-left', `${context.insets.left}px`);
  }, [context.isMobileApp, context.insets, isFakeMobileApp]);

  useEffect(() => {
    document.addEventListener('message', resolveWebViewMessage);

    return () => document.removeEventListener('message', resolveWebViewMessage);
  }, []);

  const resolveWebViewMessage = (message: any): void => {
    if (message?.data?.type === OnlineShopWebViewMessageTypes.SetPushNotificationRequestStatus) {
      setPushNotificationsStatus(message.data.payload);
    }
  };

  return <MobileAppContext.Provider value={context}>{children}</MobileAppContext.Provider>;
}
